import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { UnitSystem } from '../../utils/units.js'

@customElement('measure-tools-bar')
export class MeasureToolsBar extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      padding: 0 16px;
      box-sizing: border-box;
      font-size: 13px;
      font-weight: 500;
      color: white;
    }

    .measure-info {
      display: flex;
      gap: 16px;
      align-items: center;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
    }

    .info-label {
      font-size: 10px;
      opacity: 0.7;
      line-height: 1;
    }

    .info-value {
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
    }

    .info-item.left {
      align-items: flex-start;
    }

    .hint {
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
    }

    .back-button {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      padding: 4px;
      margin-right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background 0.15s ease;
    }

    .back-button:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
    }
  `

  @property({ type: String })
  unitSystem: UnitSystem = 'imperial'

  @property({ type: String })
  distance?: string

  @property({ type: String })
  bearing?: string

  @property({ type: String })
  area?: string

  @property({ type: String })
  perimeter?: string

  @property({ type: String })
  measureType: 'none' | 'line' | 'shape' | 'point' = 'none'

  @property({ type: String })
  location?: string

  private handleClear(): void {
    this.dispatchEvent(new CustomEvent('measure-clear', { bubbles: true }))
  }

  override render(): unknown {
    const hasResult =
      this.measureType !== 'none' &&
      ((this.measureType === 'line' && !!this.distance) ||
        (this.measureType === 'point' && !!this.location) ||
        (this.measureType === 'shape' && !!(this.area ?? this.perimeter)))

    if (this.measureType === 'line' && this.distance) {
      return html`
        ${hasResult
          ? html`<button class="back-button" @click=${this.handleClear} title="Clear">
              <span class="material-icons">arrow_back</span>
            </button>`
          : ''}
        <div class="measure-info">
          <div class="info-item">
            <div class="info-label">DISTANCE</div>
            <div class="info-value">${this.distance}</div>
          </div>
          ${this.bearing
            ? html`
                <div class="info-item">
                  <div class="info-label">BEARING</div>
                  <div class="info-value">${this.bearing}</div>
                </div>
              `
            : ''}
        </div>
      `
    }

    if (this.measureType === 'point' && this.location) {
      return html`
        ${hasResult
          ? html`<button class="back-button" @click=${this.handleClear} title="Clear">
              <span class="material-icons">arrow_back</span>
            </button>`
          : ''}
        <div class="measure-info">
          <div class="info-item left">
            <div class="info-label">LOCATION</div>
            <div class="info-value">${this.location}</div>
          </div>
        </div>
      `
    }

    if (this.measureType === 'shape') {
      return html`
        ${hasResult
          ? html`<button class="back-button" @click=${this.handleClear} title="Clear">
              <span class="material-icons">arrow_back</span>
            </button>`
          : ''}
        <div class="measure-info">
          ${this.area
            ? html`
                <div class="info-item">
                  <div class="info-label">AREA</div>
                  <div class="info-value">${this.area}</div>
                </div>
              `
            : ''}
          ${this.perimeter
            ? html`
                <div class="info-item">
                  <div class="info-label">PERIMETER</div>
                  <div class="info-value">${this.perimeter}</div>
                </div>
              `
            : ''}
        </div>
      `
    }

    return html`<div class="hint">Tap map to measure distance, or tap a shape for area</div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'measure-tools-bar': MeasureToolsBar
  }
}
